import "./simple-pdf-tool.css";
import "./ocr-pdf.css";
import "./pdf-to-text.css";
import { openBrowserPdf, pdfErrorMessage, renderPageBlob } from "./pdf-browser-core.js";
import { selectedOcrLanguages } from "./ocr-pdf-core.js";
import { createOperationResult } from "./operation-result.js";
import {
  PDF_TO_TEXT_LIMITS, assertAutomaticOcrPageLimit, choosePageText, countTextContent, extractOcrPageText,
  extractPositionedPageText, formatTextPages, hasMeaningfulPdfText, pdfTextFileName
} from "./pdf-to-text-core.js";

const root = document.querySelector("[data-pdf-to-text]");

if (root) {
  const ids = [
    "pdfTextStatus", "pdfTextUploadPanel", "pdfTextDropZone", "choosePdfTextFile", "pdfTextFileInput",
    "pdfTextWorkspace", "pdfTextFileName", "pdfTextPageCount", "replacePdfTextFile", "pdfTextPreview",
    "pdfTextSetup", "pdfTextAutomaticOcr", "pdfTextLanguage", "pdfTextIncludeEnglish", "extractPdfText",
    "cancelPdfText", "pdfTextProgress", "pdfTextProgressBar", "pdfTextEditorPanel", "pdfTextOutput",
    "pdfTextCharacterCount", "pdfTextLineCount", "copyPdfText", "preparePdfText"
  ];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = {
    document: null,
    fileName: "document.pdf",
    pageCount: 0,
    busy: false,
    cancelling: false,
    previewUrl: null,
    ocrModule: null,
    currentOcrIndex: 0,
    ocrCandidates: 0
  };
  const operationResult = createOperationResult(root, elements.pdfTextWorkspace);

  function showStatus(message = "") {
    elements.pdfTextStatus.textContent = message;
    elements.pdfTextStatus.hidden = !message;
  }

  function setProgress(message = "", value = null) {
    elements.pdfTextProgress.textContent = message;
    elements.pdfTextProgress.hidden = !message;
    elements.pdfTextProgressBar.hidden = !message;
    if (value == null) elements.pdfTextProgressBar.removeAttribute("value");
    else elements.pdfTextProgressBar.value = Math.max(0, Math.min(1, value));
  }

  function updateControls() {
    const automaticOcr = elements.pdfTextAutomaticOcr.checked;
    elements.replacePdfTextFile.disabled = state.busy;
    elements.pdfTextAutomaticOcr.disabled = state.busy;
    elements.pdfTextLanguage.disabled = state.busy || !automaticOcr;
    elements.pdfTextIncludeEnglish.disabled = state.busy || !automaticOcr || elements.pdfTextLanguage.value === "eng";
    elements.extractPdfText.disabled = state.busy || !state.document;
    elements.cancelPdfText.hidden = !state.busy;
    elements.pdfTextOutput.disabled = state.busy;
    elements.copyPdfText.disabled = state.busy || !elements.pdfTextOutput.value;
    elements.preparePdfText.disabled = state.busy || !elements.pdfTextOutput.value;
  }

  async function updatePreview() {
    const page = await state.document.getPage(1);
    try {
      const blob = await renderPageBlob(page, { dpi: 110, maxWidth: 560, maxHeight: 560 });
      if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
      state.previewUrl = URL.createObjectURL(blob);
      elements.pdfTextPreview.src = state.previewUrl;
    } finally {
      page.cleanup();
    }
  }

  async function loadFile(file) {
    if (state.busy) return;
    showStatus();
    state.busy = true;
    updateControls();
    let loaded = null;
    try {
      loaded = await openBrowserPdf(file, PDF_TO_TEXT_LIMITS);
      await releaseDocument();
      state.document = loaded.document;
      state.fileName = loaded.fileName;
      state.pageCount = loaded.pageCount;
      loaded = null;
      elements.pdfTextFileName.textContent = state.fileName;
      elements.pdfTextPageCount.textContent = String(state.pageCount);
      elements.pdfTextOutput.value = "";
      elements.pdfTextEditorPanel.hidden = true;
      elements.pdfTextSetup.hidden = false;
      elements.pdfTextUploadPanel.hidden = true;
      elements.pdfTextWorkspace.hidden = false;
      updateCounts();
      await updatePreview();
    } catch (error) {
      if (loaded?.document) await loaded.document.destroy();
      showStatus(pdfErrorMessage(error));
    } finally {
      state.busy = false;
      elements.pdfTextFileInput.value = "";
      updateControls();
    }
  }

  async function extractText() {
    if (state.busy || !state.document) return;
    state.busy = true;
    state.cancelling = false;
    state.ocrCandidates = 0;
    showStatus();
    setProgress("Reading page 1…", 0);
    updateControls();
    window.toolboxAnalytics?.track("tool_start");
    let session = null;
    try {
      const pages = [];
      const candidates = [];
      for (let pageNumber = 1; pageNumber <= state.pageCount; pageNumber += 1) {
        throwIfCancelled();
        setProgress(`Reading page ${pageNumber} of ${state.pageCount}…`, (pageNumber - 1) / state.pageCount * .45);
        const page = await state.document.getPage(pageNumber);
        try {
          const content = await page.getTextContent();
          const selectableText = extractPositionedPageText(content.items);
          const entry = { pageNumber, text: selectableText };
          pages.push(entry);
          if (!hasMeaningfulPdfText(content.items)) candidates.push(entry);
        } finally {
          page.cleanup();
        }
      }

      state.ocrCandidates = candidates.length;
      if (elements.pdfTextAutomaticOcr.checked) assertAutomaticOcrPageLimit(candidates.length);

      if (elements.pdfTextAutomaticOcr.checked && candidates.length) {
        setProgress("Loading OCR language data…", .45);
        state.ocrModule ||= await import("./pdf-editor-ocr.js");
        const languages = selectedOcrLanguages(elements.pdfTextLanguage.value, elements.pdfTextIncludeEnglish.checked);
        session = await state.ocrModule.createPdfOcrSession(languages, updateRecognitionProgress);
        for (let index = 0; index < candidates.length; index += 1) {
          throwIfCancelled();
          state.currentOcrIndex = index;
          const candidate = candidates[index];
          const page = await state.document.getPage(candidate.pageNumber);
          try {
            const lines = await session.recognizePage(page, { layout: "lines" });
            candidate.text = choosePageText(candidate.text, extractOcrPageText(lines));
          } finally {
            page.cleanup();
          }
        }
        await session.terminate();
        session = null;
      }

      throwIfCancelled();
      setProgress("Preparing the editable text…", .98);
      const output = formatTextPages(pages);
      const counts = countTextContent(output);
      if (!counts.contentCharacters) {
        throw new Error("No text was found. Try enabling automatic OCR, selecting the document language, or use a clearer scan.");
      }
      elements.pdfTextOutput.value = output;
      elements.pdfTextSetup.hidden = true;
      elements.pdfTextEditorPanel.hidden = false;
      updateCounts();
      await releaseDocument();
      elements.pdfTextOutput.focus();
      showStatus(candidates.length
        ? `Text extracted. Automatic OCR checked ${candidates.length} page${candidates.length === 1 ? "" : "s"} without enough selectable text.`
        : "Text extracted. Review or edit it before preparing the TXT file.");
    } catch (error) {
      if (!state.cancelling && error?.code !== "OCR_CANCELLED") {
        showStatus(error?.message || "smallpdf.app could not extract text from this PDF.");
      }
    } finally {
      if (session) await session.terminate();
      state.busy = false;
      state.cancelling = false;
      state.currentOcrIndex = 0;
      setProgress();
      updateControls();
    }
  }

  function updateRecognitionProgress(progress) {
    const pageProgress = Math.max(0, Math.min(1, Number(progress?.progress) || 0));
    const ocrProgress = state.ocrCandidates
      ? (state.currentOcrIndex + pageProgress) / state.ocrCandidates
      : pageProgress;
    const pageNumber = state.currentOcrIndex + 1;
    setProgress(
      `${progress?.status || "Recognizing text"} · OCR page ${pageNumber} of ${state.ocrCandidates}`,
      .45 + ocrProgress * .5
    );
  }

  function updateCounts() {
    const counts = countTextContent(elements.pdfTextOutput.value);
    elements.pdfTextCharacterCount.textContent = counts.characters.toLocaleString("en-US");
    elements.pdfTextLineCount.textContent = counts.lines.toLocaleString("en-US");
    updateControls();
  }

  async function copyText() {
    const value = elements.pdfTextOutput.value;
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      showStatus("Text copied to the clipboard.");
    } catch {
      elements.pdfTextOutput.focus();
      elements.pdfTextOutput.select();
      showStatus("Clipboard access is unavailable. The text is selected so you can copy it manually.");
    }
  }

  function prepareTextFile() {
    const value = elements.pdfTextOutput.value;
    if (!value.trim()) {
      showStatus("Add some text before preparing the TXT file.");
      return;
    }
    if (value.length > PDF_TO_TEXT_LIMITS.outputCharacters) {
      showStatus(`The edited text is longer than the ${PDF_TO_TEXT_LIMITS.outputCharacters.toLocaleString("en-US")} character limit.`);
      return;
    }
    const counts = countTextContent(value);
    operationResult.show({
      blob: new Blob([value], { type: "text/plain;charset=utf-8" }),
      fileName: pdfTextFileName(state.fileName),
      message: "Your reviewed PDF text is ready as a UTF-8 plain-text file.",
      details: [
        `${state.pageCount} page${state.pageCount === 1 ? "" : "s"}`,
        `${counts.characters.toLocaleString("en-US")} characters`,
        `${counts.lines.toLocaleString("en-US")} lines`
      ],
      downloadLabel: "Download TXT"
    });
  }

  function throwIfCancelled() {
    if (!state.cancelling) return;
    const error = new Error("OCR_CANCELLED");
    error.code = "OCR_CANCELLED";
    throw error;
  }

  async function cancel() {
    if (!state.busy || state.cancelling) return;
    state.cancelling = true;
    setProgress("Cancelling…");
    await state.ocrModule?.cancelPdfOcr();
  }

  async function releaseDocument() {
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    state.previewUrl = null;
    if (state.document) await state.document.destroy();
    state.document = null;
  }

  elements.choosePdfTextFile.addEventListener("click", event => {
    event.stopPropagation();
    elements.pdfTextFileInput.click();
  });
  elements.replacePdfTextFile.addEventListener("click", () => elements.pdfTextFileInput.click());
  elements.pdfTextFileInput.addEventListener("change", () => loadFile(elements.pdfTextFileInput.files?.[0]));
  elements.extractPdfText.addEventListener("click", extractText);
  elements.cancelPdfText.addEventListener("click", cancel);
  elements.copyPdfText.addEventListener("click", copyText);
  elements.preparePdfText.addEventListener("click", prepareTextFile);
  elements.pdfTextOutput.addEventListener("input", updateCounts);
  elements.pdfTextAutomaticOcr.addEventListener("change", updateControls);
  elements.pdfTextLanguage.addEventListener("change", () => {
    if (elements.pdfTextLanguage.value === "eng") elements.pdfTextIncludeEnglish.checked = false;
    updateControls();
  });
  elements.pdfTextDropZone.addEventListener("click", event => {
    if (!event.target.closest("button")) elements.pdfTextFileInput.click();
  });
  elements.pdfTextDropZone.addEventListener("keydown", event => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      elements.pdfTextFileInput.click();
    }
  });
  elements.pdfTextDropZone.addEventListener("dragover", event => {
    event.preventDefault();
    elements.pdfTextDropZone.classList.add("is-dragging");
  });
  elements.pdfTextDropZone.addEventListener("dragleave", () => elements.pdfTextDropZone.classList.remove("is-dragging"));
  elements.pdfTextDropZone.addEventListener("drop", event => {
    event.preventDefault();
    elements.pdfTextDropZone.classList.remove("is-dragging");
    loadFile(event.dataTransfer.files?.[0]);
  });
  window.addEventListener("beforeunload", () => {
    operationResult.dispose();
    state.ocrModule?.cancelPdfOcr();
    releaseDocument();
  });
  updateCounts();
}
